import * as React from 'react'

import { useFormContext, type RegisterOptions } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { AppIcon } from '@/shared/ui/icons'

import { controlVariants, type ControlSize } from './control-styles'
import { useMergedRef } from './lib/merge-refs'
import { useControlName } from './lib/use-control-name'
import { useLegacyFieldAria } from './lib/use-legacy-field-aria'

/**
 * Campo de senha com alternar visibilidade.
 *
 * Dois cuidados que este controle costuma errar:
 *
 * 1. O botão precisa de **nome acessível que reflita o estado atual** — "Mostrar
 *    senha" quando está oculta, "Ocultar senha" quando está visível. Um rótulo
 *    fixo deixa quem usa leitor de tela sem saber o que vai acontecer.
 * 2. O alvo de toque do botão é próprio e tem 44×44 (`--target-min`), sem comer
 *    a área clicável do campo. O `pr` do input reserva esse espaço.
 */

type PasswordFieldProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'size' | 'name' | 'type'
> & {
  size?: ControlSize
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  /** Regras do react-hook-form. Prefira validar por schema; isto existe para
   *  paridade com os campos legados durante a migração. */
  rules?: RegisterOptions
}

export const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ className, size, name: nameProp, rules, onChange, onBlur, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false)
    const name = useControlName(nameProp)
    const legacyAria = useLegacyFieldAria(name)
    const { register } = useFormContext()
    const { ref: registerRef, onChange: registerChange, onBlur: registerBlur, ...field } =
      register(name, rules)

    // O handler do consumidor é composto com o do register, nunca o substitui:
    // espalhar `props` por cima de `field` faria um `onChange` de fora derrubar
    // o registro do formulário, e o campo pararia de gravar valor em silêncio.
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      void registerChange(event)
      onChange?.(event)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      void registerBlur(event)
      onBlur?.(event)
    }

    const mergedRef = useMergedRef(registerRef, ref)

    return (
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          className={cn(
            controlVariants({ size }),
            'pr-[var(--target-min)]',
            className
          )}
          {...legacyAria}
          {...field}
          {...props}
          onChange={handleChange}
          onBlur={handleBlur}
          ref={mergedRef}
        />
        <button
          type="button"
          // O rótulo acompanha o estado: anuncia a ação, não o ícone.
          aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
          aria-pressed={visible}
          onClick={() => setVisible(current => !current)}
          className={cn(
            'absolute right-0 top-1/2 -translate-y-1/2',
            'flex h-[var(--target-min)] w-[var(--target-min)] items-center justify-center',
            'text-gray-700 hover:text-gray-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
            'rounded-[var(--control-r-md)]'
          )}
        >
          <AppIcon name={visible ? 'eye-slash' : 'eye'} size={18} />
        </button>
      </div>
    )
  }
)
PasswordField.displayName = 'PasswordField'
